//@ts-nocheck
import React, { useCallback, useRef, Suspense, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  Pagination,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Add as AddIcon,
  KeyboardArrowRight,
  NavigateNext,
} from "@mui/icons-material";
import { useGetToken } from "../../hooks/useGetToken";
import axios, { AxiosRequestConfig } from "axios";
import CircleIcon from "@mui/icons-material/Circle";
import ClearIcon from "@mui/icons-material/Clear";
import AdminInstallerForm from "../AdminInstallerForm";
import ProfileWithQual from "../../containers/ProfileWithQual";
import "./ScrollableTable.css";
import Header from "../Header";
import SubHeader from "../SubHeader";
import Loading from "../Loading";
import BreadcrumbNavigation from "../BreadcrumbNavigation";
import { useGlobalStateContext } from "../../context/globalState";
import TrainingsLevelView, {
  TrainingHeadersType,
} from "../TrainingsHeaderView";
import { Tooltip } from "@mui/material";
import { load } from "dotenv";
import AlertBox from "../AlertBox";
import TextLabel from "../TextLabel";
import { CONSTANTS } from "../../CONSTANTS";
import {
  formatTrainingTypesResponse,
  TrainingTypesResponseType,
} from "../helper";
import Context from "../../context";

const BulkUploadWrapper = React.lazy(() => import("../BulkUpload"));

interface ScrollableTableProps {
  onSignOut: () => void;
}

type CompanyType = {
  id: string | number;
  firstName: string;
  lastName: string;
  country: string;
  city?: string;
};
type QualificationStatus = {
  userId: number;
  qualificationTypeId: number;
  expirationDate: string;
  state: string;
  trainingId: number;
};

const ScrollableTable: React.FC<ScrollableTableProps> = ({ onSignOut }) => {
  // Reading from global state context
  const [getToken] = useGetToken();
  const { t, i18n } = useTranslation();
  const rowsPerPage = process.env.REACT_APP_ROWS_PER_PAGE || 20;

  const {
    adminRole,
    countryList,
    setCountryList,
    country,
    setCountry,
    frontline,
    frontlineId,
    setFrontline,
    regionId,
    setRegionId,
    supplierType,
    formattedCountryCodes,
    trainingHeaders,
    loading,
    setLoading,
    beginLoading,
    endLoading,
    alertState,
    alertMessage,
    setAlertMessage,
    setAlertState,
    setTrainingTypes,
    userPreference,
    setUserPreference,
    breadcrumbs,
    setBreadcrumbs,
    setIsBreadcrumbClicked,
    selectedBusinessTypeId,
  } = useGlobalStateContext();

  const {
    authState: { user },
  } = useContext(Context);

  // State variables
  const [open, setOpen] = useState<{ [key: string]: boolean }>({});
  const [companyList, setCompanyList] = React.useState<CompanyType[]>([]);
  const [selectedCompany, setSelectedCompany] = React.useState(null);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [trainingData, setTrainingData] = React.useState([]);
  const [hovered, setHovered] = React.useState("");
  const [openForm, setOpenForm] = React.useState(false);
  const [data, setData] = React.useState<Installer[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedData, setSelectedData] = React.useState(false);
  const [selectedFitterUserId, setSelectedFitterUserId] = React.useState<
    string | null
  >(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [fitters, updateFitters] = React.useState<Installer[]>([]);
  const [searchText, setSearchText] = React.useState("");
  const [filteredFitters, setFilteredFitters] = React.useState<Installer[]>([]);
  const [qualificationTypeList, setQualificationTypeList] = useState([]);
  const [trainingView, setTrainingView] = useState<any>(null); // Store training data
  const [selectedQualificationType, setSelectedQualificationType] =
    useState<any>(null); // Track selected qualification type for row toggle
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [isOverallView, setIsOverallView] = useState<boolean>(true);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [cursor, setCursor] = useState<string>("");
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [fetchingSuppliers, setFetchingSuppliers] = useState<boolean>(false);
  const [showBulkUploadForm, setShowBulkUploadForm] = useState(false);
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  //this state has current level of param for qualification status api
  const [currentParams, setCurrentParams] = useState<{
    key: string;
    value: number | undefined;
  }>({ key: "", value: undefined });
  // Ref for Intersection Observer to detect last item scroll
  const lastItemObserverRef = useRef<IntersectionObserver | null>(null);

  // Guards for the preference-persist effect below: skip the initial mount/seed
  // and the one-time backend hydration so only user-driven changes are POSTed.
  const isPreferenceHydratedRef = useRef(false);
  const suppressNextPreferencePersistRef = useRef(false);
  // Set once the user starts editing breadcrumbs. The /userSetting GET below is
  // async and can resolve after a local edit; if it does, we must NOT overwrite
  // the user's selection with the older server snapshot.
  const hasUserEditedPreferenceRef = useRef(false);

  useEffect(() => {
    const fetchUserPreference = async () => {
      const token = await getToken();
      const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/userSetting?id=${user?.username}`;
      const config: AxiosRequestConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      try {
        const response = await axios.get(uri, config);
        if (response.data.length > 0) {
          // If the user already started editing breadcrumbs while this request
          // was in flight, discard the stale server snapshot so we don't clobber
          // their local selection with older data.
          if (hasUserEditedPreferenceRef.current) return;
          // This update comes from the backend, not a user edit — don't echo it
          // straight back to the API via the persist effect below.
          suppressNextPreferencePersistRef.current = true;
          setUserPreference((prevState) => ({
            ...prevState,
            languagePreference: response.data[0].language,
            businessType: response.data[0].businessType ?? null,
            area: response.data[0].area ?? null,
            frontline: response.data[0].frontline ?? null,
            country: response.data[0].country ?? null,
            supplierType: response.data[0].supplierType ?? null,
          }));
          // Assuming `setUserPreference` is available in the context
        }
      } catch (err) {
        console.error("Failed to fetch user preference:", err);
      }
    };

    fetchUserPreference();
  }, []);

  // Persist breadcrumb-driven preference changes (business type, area, frontline,
  // country, supplier type) to the backend. Language is persisted separately by
  // the Settings/Admin pages, so it is intentionally excluded here.
  useEffect(() => {
    if (!isPreferenceHydratedRef.current) {
      // Ignore the initial mount/seed (languagePreference only).
      isPreferenceHydratedRef.current = true;
      return;
    }
    if (suppressNextPreferencePersistRef.current) {
      // Ignore the one-time hydration from GET /userSetting.
      suppressNextPreferencePersistRef.current = false;
      return;
    }

    // Past both guards: this change is user-driven. Mark the preference dirty so
    // a late-arriving /userSetting response cannot overwrite the local edit.
    hasUserEditedPreferenceRef.current = true;

    const { businessType, area, frontline, country, supplierType } =
      userPreference;
    const isPresent = (value: unknown) =>
      Array.isArray(value)
        ? value.length > 0
        : value !== undefined && value !== null && value !== "";
    // Only persist once every preference field is selected; a single missing
    // value (null/empty, or no country selected) skips the call entirely.
    const hasAllValues = [
      businessType,
      area,
      frontline,
      country,
      supplierType,
    ].every(isPresent);
    if (!hasAllValues || !user?.username) return;

    const persistPreference = async () => {
      try {
        const token = await getToken();
        await axios.post(
          `${process.env.REACT_APP_API_ENDPOINT}/v1/userSetting/addPreference`,
          {
            id: user.username,
            businessType,
            area,
            frontline,
            country,
            supplierType,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          },
        );
      } catch (err) {
        console.error("Failed to persist user preference:", err);
      }
    };

    persistPreference();
  }, [
    userPreference.businessType,
    userPreference.area,
    userPreference.frontline,
    userPreference.country,
    userPreference.supplierType,
  ]);

  useEffect(() => {
    const calculateHeight = () => {
      const headerHeight = document.querySelector(".header")?.clientHeight || 0;
      const breadcrumbsHeight =
        document.querySelector(".breadcrumbs")?.clientHeight || 0;
      const alertBoxHeight =
        document.querySelector(".alert-box")?.clientHeight || 0;
      const totalHeight =
        headerHeight + breadcrumbsHeight + alertBoxHeight + 200; // Add some padding
      setTableHeight(window.innerHeight - totalHeight);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, []);

  const initialBreadcrumbs = [
    { key: "", value: 0, label: t("adminDashboard.breadcrumb.qualification") },
  ];
  useEffect(() => {
    setBreadcrumbs(initialBreadcrumbs);
  }, []);

  const [qualificationStatus, setQualificationStatus] = useState<
    QualificationStatus[]
  >([]);

  interface Installer {
    first_name: string;
    user_id: string;
    name: string;
    company_id: string;
    vendor_id: string;
    qualification: any[];
    email: string | { email: string };
  }

  useEffect(() => {
    fetchTrainingData();
    fetchFrontlineData();
    fetchTrainingTypes();
  }, []);

  const getUserQualificationStatusOnHeaderLevelChange = async (
    paramKey: string,
    paramId: number | undefined,
  ) => {
    const users = filteredFitters.length > 0 ? filteredFitters : fitters;
    setQualificationStatus([]); // Reset qualification status
    await fetchUserQualificationStatus(users, paramKey, paramId);
  };

  const handleSearch = (searchKey, searchValue) => {
    if (searchKey && searchValue) {
      setSearchText(`${searchKey}:${searchValue}`);
    } else {
      setSearchText("");
      setFilteredFitters([]);
    }
    setPage(1); // Reset to the first page
  };
  React.useEffect(() => {
    formattedCountryCodes.length > 0 &&
      supplierType &&
      (supplierType === "vendor"
        ? fetchVendorsData(formattedCountryCodes)
        : fetchKoneCompanyData(formattedCountryCodes));
  }, [formattedCountryCodes, rowsPerPage, searchText]);

  // Re-fetch data when business type changes
  React.useEffect(() => {
    if (
      selectedBusinessTypeId &&
      formattedCountryCodes.length > 0 &&
      supplierType
    ) {
      resetCompanyList();
      setSelectedCompany(null);
      // Re-fetch company/vendor list
      supplierType === "vendor"
        ? fetchVendorsData(formattedCountryCodes)
        : fetchKoneCompanyData(formattedCountryCodes);
    }
  }, [selectedBusinessTypeId]);

  const resetCompanyList = () => {
    // Reset the company list and all user data to initial state
    setCompanyList([]);
    setCursor("");
    setHasMore(false);
    setOpen({});
    setData([]);
    updateFitters([]);
    setFilteredFitters([]);
    if (lastItemObserverRef.current) {
      lastItemObserverRef.current.disconnect();
      lastItemObserverRef.current = null;
    }
  };

  interface Training {
    trainingId: string;
    trainingName: string;
    trainingType: string;
    passportLevel: number;
  }

  const categorizedTrainingData = {
    safety: trainingData.filter(
      (training: Training) => training.trainingType === "GLOBAL_SAFETY",
    ),
    local: trainingData.filter(
      (training: Training) => training.trainingType === "LOCAL_SAFETY",
    ),
    method: trainingData.filter(
      (training: Training) => training.trainingType === "METHOD",
    ),
  };

  const group1Span = categorizedTrainingData?.safety?.length;
  const group2Span = categorizedTrainingData?.local?.length;
  const group3Span = categorizedTrainingData?.method?.length;

  const totalColumns = group1Span + group2Span + group3Span;

  const toggleForm = (e?: React.MouseEvent, rowId?: string) => {
    // The credentials dialog closes via onCloseCb() with no event, so guard it.
    e?.preventDefault();
    e?.stopPropagation();
    setOpenForm((prev) => !prev);
  };

  const openAddInstallerForm = () => {
    setOpenForm(true);
  };

  const fetchTrainingTypes = async () => {
    const loadingToken = beginLoading("scrollable-table-training-types");
    const token = await getToken();
    const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/trainingTypes`;
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };
    try {
      const response = await axios.get(uri, config);
      setTrainingTypes(
        formatTrainingTypesResponse(response.data as TrainingTypesResponseType),
      );
    } catch (err) {
      setError("Failed to fetch training types");
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchData = async (companyId: string) => {
    if (loading) return; // Prevent multiple requests
    const loadingToken = beginLoading("scrollable-table-company-installers");
    const token = await getToken();
    // Add businessType query param - default to DELIVERY if not selected
    const businessTypeValue = selectedBusinessTypeId || 100; // Default to 100 if no business type is selected
    const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/installers/${companyId}?businessTypeId=${businessTypeValue}`;
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };

    if (!companyId) {
      endLoading(loadingToken);
      return;
    }

    try {
      const response = await axios.get(uri, config);
      const { users = [] } = response.data;
      setData(users);
      updateFitters(users);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchVendorsData = async (country) => {
    if (loading) return; // Prevent multiple requests
    const loadingToken = !fetchingSuppliers
      ? beginLoading("scrollable-table-vendors")
      : null;
    const token = await getToken();

    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const appendCursor = cursor ? `&cursor=${cursor}` : "";

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/company/getVendors?countries=${country}&frontline=${frontlineId}&limit=${rowsPerPage}${appendCursor}&filter=${searchText}`, // page,totalPage and limit (optional) (15 by default) to be added while implementing pagination
        config,
      );

      const vendors = response.data.vendors;
      const subUsers =
        response.data.users && Object.values(response.data.users).length > 0
          ? Object.values(response.data.users).flat()
          : [];
      if (subUsers && subUsers.length > 0) {
        setFilteredFitters(subUsers);
      }
      // Replace list on fresh fetch (no cursor), append on pagination (has cursor)
      if (cursor) {
        setCompanyList((prev) => [...prev, ...vendors]);
      } else {
        setCompanyList(vendors);
      }
      setCursor(response.data.nextCursor);
      if (!response.data.nextCursor) {
        setHasMore(false);
      } else if (!hasMore) {
        setHasMore(true);
      }
      setOpen({});
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (loadingToken) {
        endLoading(loadingToken);
      }
    }
  };
  const fetchKoneCompanyData = async (country) => {
    const token = await getToken();
    const loadingToken = !loading
      ? beginLoading("scrollable-table-kone-company")
      : null;
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };
      const appendCursor = cursor ? `&cursor=${cursor}` : "";
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/company/getCompany?countries=${country}&limit=${rowsPerPage}${appendCursor}&filter=${searchText}&businessTypeId=${selectedBusinessTypeId}&lang=${userPreference?.languagePreference || "en"}`, // page,totalPage and limit (optional) (15 by default) to be added while implementing pagination
        config,
      );

      const koneCompanies = response.data.companies;
      const koneUsers =
        response.data.users && Object.values(response.data.users).length > 0
          ? Object.values(response.data.users).flat()
          : [];
      if (koneUsers && koneUsers.length > 0) {
        setFilteredFitters(koneUsers);
      }
      setCompanyList(koneCompanies);
      setCursor(response.data.nextCursor);
      if (!response.data.nextCursor) {
        setHasMore(false);
      } else if (!hasMore) {
        setHasMore(true);
      }
      setOpen({});
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (loadingToken) {
        endLoading(loadingToken);
      }
    }
  };
  const fetchTrainingData = async () => {
    const token = await getToken();
    const loadingToken = beginLoading("scrollable-table-trainings");
    const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/trainings`;
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };

    try {
      const response = await axios.get(uri, config);
      setTrainingData(response.data);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchSuppliersByType = async () => {
    if (supplierType === "vendor") {
      await fetchVendorsData(formattedCountryCodes);
    } else if (supplierType === "supplier") {
      await fetchKoneCompanyData(formattedCountryCodes);
    }
    if (fetchingSuppliers) {
      setFetchingSuppliers(false);
    }
  };

  // Function to observe when the last item is in view
  const lastSupplierElementRef = (node: HTMLElement) => {
    if (loading) return; // Don't observe while loading
    if (lastItemObserverRef.current) lastItemObserverRef.current.disconnect(); // Clear previous observer

    lastItemObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setFetchingSuppliers(true);
          fetchSuppliersByType();
        } else {
          setFetchingSuppliers(false);
        }
      },
      { threshold: 1.0 },
    );

    if (node) lastItemObserverRef.current.observe(node);
  };

  const fetchUserQualificationStatus = async (
    users: Installer[],
    paramKey?: string,
    paramId?: number | undefined,
  ) => {
    const token = await getToken();
    const loadingToken = beginLoading("scrollable-table-qualification-status");
    const qualificationQuery =
      paramKey && paramId ? `&${paramKey}=${paramId}` : "";

    // Setting params for current level even before the user qualification call
    // Because when we expand the header even before selecting the country or Kone Internal / Subcontractor,
    // we want to keep track of the current parameters for fetching qualification status, if
    // Country or Kone Internal / Subcontractor is selected later after expanding the header
    setCurrentParams({ key: paramKey || "", value: paramId });

    // Handle both user_id (from normal fetch) and id (from search results)
    const userIds = users
      .map((user) => user.user_id || user.id)
      .filter(Boolean)
      .join(",");
    if (!userIds) {
      endLoading(loadingToken);
      return;
    }
    const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/trainings/qualificationStatus?userIds=${userIds}${qualificationQuery}`;
    // const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/trainings/qualificationStatus?userIds=1,2,3`;
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };

    try {
      const response = await axios.get(uri, config);
      setQualificationStatus(response.data);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      endLoading(loadingToken);
    }
  };

  const handleMouseEnter = (title: string, userId: string) => {
    setHovered(title + "" + userId);
  };
  const handleMouseLeave = () => {
    setHovered("");
  };
  function StatusIcon(
    status: string | undefined,
    expiry: string | undefined,
    title: string,
    userId: string,
  ) {
    const getIcon = (status: string | undefined) => {
      const normalizedStatus = status?.toLowerCase();
      switch (normalizedStatus) {
        case "notqualified":
          return <img className="status-icon" src="/QualificationStatus.svg" />;
        case "needaction":
          return (
            <img className="status-icon" src="/icon_status_attention.svg" />
          );
        case "warning":
          return <img className="status-icon" src="/icon_expiringsoon.jpg" />;
        case "expired":
        case "invalid":
          return <img className="status-icon" src="/icon_expired.jpg" />;
        case "active":
        case "valid":
          return <img className="status-icon" src="/icon_valid.jpg" />;
        case "notvalid":
          return (
            <img className="status-icon" src="/icon_status_notvalid.svg" />
          );
        default:
          return (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "white",
              }}
            />
          );
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          cursor: "pointer",
          transition: "all 0.3s ease",
          width: 100,
          justifyContent: "center",
          height: "100%",
        }}
        onMouseEnter={() => handleMouseEnter(title, userId)}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            transition: "all 0.3s ease",
            transform:
              hovered === title + "" + userId && expiry
                ? "translateX(-10px)"
                : "translateX(0)",
            marginRight:
              hovered === title + "" + userId && expiry ? "50px" : "0", // Add space between icon and date
          }}
        >
          {getIcon(status)}
        </Box>
        {hovered === title + "" + userId && expiry && (
          <Typography
            sx={{
              fontSize: 14,
              color: "#333",
              position: "absolute",
              left: "27px",
              top: 0,
            }}
          >
            {expiry}
          </Typography>
        )}
      </Box>
    );
  }

  const fetchFrontlineData = async () => {
    const token = await getToken();
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/frontlines/${regionId}`,
        config,
      );
      setFrontlineList(response.data);
    } catch (error) {
      console.error("Error fetching frontlines:", error);
    }
  };

  const fetchCountryData = async (frontlineId: string) => {
    const token = await getToken();
    const loadingToken = beginLoading("scrollable-table-countries");
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/countries?frontlineId=` +
          frontlineId,
        config,
      );
      setCountryList(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const handleUpdateInstallers = async (data) => {
    updateFitters(data);
  };

  const onCompanyOpen = (company: { id: string; country: string }) => {
    setOpen((prevOpen) => ({ [company.id]: !prevOpen[company.id] }));
    setData([]);
    fetchData(company.id);
    setSelectedCompany(company);
    setSelectedCountry(company.country || country[0]);
  };

  const handleRedirect = (installer: Installer) => {
    const userId = installer?.user_id;
    setSelectedFitterUserId(userId);
    setSelectedData(true);
  };
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  function addBreadcrumbItem(breadcrumbText: string, key: string, id: number) {
    console.log("Breadcrumb key to be added is ", key);
    if (key === "trainingId") {
      return;
    }
    setBreadcrumbs([...breadcrumbs, { key, value: id, label: breadcrumbText }]);
  }
  function handleBreadcrumbClick(paramsKey: string, paramId: number) {
    // Reset the breadcrumbs to the initial value
    if (paramsKey === "trainingId") {
      return;
    }
    if (breadcrumbs.length > 1) {
      // Remove the clicked breadcrumb and all breadcrumbs after it based on the paramsKey. Compare paramsKey with the key of each breadcrumb
      const updatedBreadcrumbs = breadcrumbs.slice(
        0,
        breadcrumbs.findIndex((breadcrumb) => breadcrumb.key === paramsKey) + 1,
      );
      console.log("Updated breadcrumbs: ", updatedBreadcrumbs);
      setBreadcrumbs(updatedBreadcrumbs);
    } else {
      setBreadcrumbs(initialBreadcrumbs);
    }
    setIsBreadcrumbClicked(true);
  }

  useEffect(() => {
    if (fitters.length > 0 || filteredFitters.length > 0) {
      getUserQualificationStatusOnHeaderLevelChange(
        currentParams.key,
        currentParams.value,
      );
    }
  }, [fitters, filteredFitters]);
  useEffect(() => {
    if (formattedCountryCodes && formattedCountryCodes.length > 0) {
      fetchSuppliersByType();
    }
  }, [country, supplierType]);

  const onCloseAlertBox = () => {
    setAlertState("");
    setAlertMessage("");
  };

  useEffect(() => {
    console.log(
      "Language preference changed:",
      userPreference.languagePreference,
    );
    const updatedBreadcrumbs =
      breadcrumbs.length > 1
        ? [...initialBreadcrumbs, breadcrumbs[1]]
        : initialBreadcrumbs;
    setBreadcrumbs(updatedBreadcrumbs);
  }, [userPreference.languagePreference]);

  const renderBreadcrumbs = () => (
    <>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        cursor="pointer"
      >
        {breadcrumbs}
      </Breadcrumbs>
    </>
  );

  const getStatusIconPath = (status: string) => {
    switch (status) {
      case "active":
        return "/icon_status_active.jpg";
      case "inactive":
        return "/icon_status_inactive.jpg";
      case "blocked":
        return "/icon_status_blocked.jpg";
      default:
        return "/icon_status_active.jpg";
    }
  };

  const getStatusSortOrder = (status: string) => {
    switch ((status || "").toLowerCase()) {
      case "active":
        return 0;
      case "inactive":
        return 1;
      case "blocked":
        return 2;
      default:
        return 0;
    }
  };

  const getNameStyleByStatus = (status: string) => {
    switch (status) {
      case "inactive":
        return { color: CONSTANTS.COLORS.INACTIVE_YELLOW };
      case "blocked":
        return {
          color: CONSTANTS.COLORS.BLOCKED_RED,
          textDecoration: "line-through",
        };
      case "active":
        return { color: "inherit" }; // Default style for active status
      default:
        return { color: "inherit" }; // Default style for unknown status
    }
  };

  return (
    <div
      style={{
        order: "1px solid",
        left: "0px",
        // width: '1280px',
        /* UI Properties */
        background: " #FFFFFF 0% 0% no-repeat padding-box",
        opacity: "1",
      }}
    >
      <div
        style={{
          position: "sticky",
          left: "0",
          zIndex: 1,
          left: "0.5px",
          // width: '1280px',
          height: "59px",
          /* UI Properties */
          opacity: "1",
        }}
      >
        <Header adminRole={adminRole} onSignOut={onSignOut} />
      </div>
      {adminRole ? (
        <>
          <div
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              position: "sticky",
              left: "0",
              zIndex: 1,
            }}
          >
            <BreadcrumbNavigation
              resetCompanyList={resetCompanyList}
              adminRole={adminRole}
            />
          </div>

          {alertState && (
            <div
              style={{
                marginTop: "20px",
                marginBottom: "10px",
                position: "sticky",
                left: "0",
                zIndex: 200,
              }}
            >
              <AlertBox
                type={alertState}
                message={alertMessage}
                onCloseCb={onCloseAlertBox}
              />
            </div>
          )}

          <div
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              position: "sticky",
              left: "0",
              zIndex: 1,
            }}
          >
            <SubHeader
              handleOpenDialog={handleOpenDialog}
              handleSearch={handleSearch}
              renderBreadcrumbs={renderBreadcrumbs}
              renderAddInstallerForm={openAddInstallerForm}
              showBulkUploadForm={showBulkUploadForm}
              setShowBulkUploadForm={setShowBulkUploadForm}
              handleBreadcrumbClick={handleBreadcrumbClick}
              adminRole={adminRole}
              countryList={countryList}
            />
          </div>

          {/*
            The upload / download icons in the SubHeader. Upload takes an Excel
            of users; download hands out the user and training templates. The
            in-app grid does NOT open from here - it has its own menu entry, so
            the two ways of onboarding stay separate rather than one icon meaning
            both.
          */}
          {showBulkUploadForm && (
            <div
              style={{
                position: "absolute",
                top: 150, // Adjust as needed based on SubHeader's position/height
                right: 40, // Adjust as needed for spacing from right edge
                zIndex: 150,
                minWidth: 150,
                minHeight: 60,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Suspense fallback={<Loading />}>
                <BulkUploadWrapper
                  setShowBulkUploadForm={setShowBulkUploadForm}
                />
              </Suspense>
            </div>
          )}

          <div
            className="table-container"
            style={{
              overflow: "auto", // Enable scrolling
              maxHeight: `${tableHeight}px`, // Dynamically set height
            }}
          >
            <table className="table-wrapper">
              <thead className="sticky-header">
                <tr>
                  <th className="first-column"></th>
                  <TrainingsLevelView
                    setPageLoader={setLoading}
                    addBreadcrumbItem={addBreadcrumbItem}
                    isOverallView={isOverallView}
                    fetchUserQualificationStatus={
                      getUserQualificationStatusOnHeaderLevelChange
                    }
                  />
                </tr>
              </thead>

              <tbody>
                {companyList?.map((company, index) => (
                  <React.Fragment key={company.id}>
                    <tr>
                      <td
                        {...(index === companyList.length - 1
                          ? { ref: lastSupplierElementRef }
                          : {})}
                        style={{
                          borderBottom: "2px solid #000",
                          borderRight: "2px solid #000",
                          width: "50px",
                          backgroundColor: "#1550F5",
                          color: "white",
                          borderColor: "#B2BEB5",
                          width: "200px",
                          backgroundColor: CONSTANTS.COLORS.BRAND_BLUE,
                          color: "white",
                          borderColor: "#B2BEB5",
                          display: "flex",
                          alignItems: "left",
                          justifyContent: "space-between",
                          position: "sticky",
                          left: "0",
                          zIndex: 1,
                        }}
                      >
                        <TextLabel
                          type="regularText"
                          color={CONSTANTS.COLORS.TEXT_WHITE}
                        >
                          {company.lastName
                            ? company.firstName + " " + company.lastName
                            : company.firstName}
                          {supplierType === "vendor"
                            ? ` (${company.id})`
                            : null}
                        </TextLabel>
                        <IconButton
                          style={{ color: "white" }}
                          aria-label="expand row"
                          size="small"
                          onClick={() => onCompanyOpen(company)}
                        >
                          {open[company.id] ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                      </td>
                    </tr>
                    {open[company.id] && (
                      <>
                        {(filteredFitters.length > 0
                          ? filteredFitters
                          : fitters
                        )
                          .filter((fitter: Installer) =>
                            [
                              fitter.company_id?.toString(),
                              fitter.vendor_id?.toString(),
                            ].includes(company.id.toString()),
                          )
                          .slice()
                          .sort(
                            (a: Installer, b: Installer) =>
                              getStatusSortOrder(a.status) -
                              getStatusSortOrder(b.status),
                          )
                          .map((installer: Installer) => (
                            <>
                              <tr key={installer.user_id}>
                                <td
                                  style={{
                                    border: "1px solid black",
                                    width: "200px",
                                    backgroundColor: "#FFFFFF",
                                    borderColor: "#EEEEEE",
                                    position: "sticky",
                                    left: "0",
                                    zIndex: 1,
                                    textAlign: "left",
                                  }}
                                  onClick={() => handleRedirect(installer)}
                                >
                                  <TextLabel type="regularText" variant="body2">
                                    {/* Choose icon based on status */}
                                    <img
                                      src={getStatusIconPath(installer.status)}
                                      style={{
                                        fontFamily:
                                          "var(--unnamed-font-family-inter)",
                                        fontStyle:
                                          "var(--unnamed-font-style-normal)",
                                        fontWeight:
                                          "var(--unnamed-font-weight-normal)",
                                        fontSize: "var(--unnamed-font-size-14)",
                                        lineHeight:
                                          "var(--unnamed-line-spacing-32)",
                                        letterSpacing:
                                          "var(--unnamed-character-spacing-0)",
                                        color: "var(--unnamed-color-1450f5)",
                                        marginRight: "5px",
                                        marginLeft: "5px",
                                      }}
                                    />

                                    {/* Show tooltip only if blocked or inactive and reason available */}
                                    {["blocked", "inactive"].includes(
                                      installer.status,
                                    ) &&
                                    installer.status_reason &&
                                    installer.status_reason !== "undefined" ? (
                                      <Tooltip
                                        title={installer.status_reason}
                                        arrow
                                        placement="right-start"
                                      >
                                        <span
                                          style={{
                                            cursor: "pointer",
                                            ...getNameStyleByStatus(
                                              installer.status,
                                            ),
                                          }}
                                        >
                                          {installer.last_name}{" "}
                                          {installer.first_name}
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <span>
                                        {installer.last_name}{" "}
                                        {installer.first_name}
                                      </span>
                                    )}
                                  </TextLabel>
                                </td>

                                {/* Training qualification cells */}
                                {trainingHeaders.map(
                                  (header: TrainingHeadersType) => {
                                    // Handle both user_id (normal fetch) and id (search results)
                                    const installerId =
                                      installer.user_id || installer.id;
                                    const qualification =
                                      qualificationStatus?.find(
                                        (qual) =>
                                          qual.id === header.id &&
                                          qual.userId === installerId,
                                      );

                                    return (
                                      <td
                                        key={
                                          isOverallView
                                            ? qualification?.qualificationTypeId
                                            : qualification?.id
                                        }
                                        style={{
                                          border: "1px solid black",
                                          width: "50px",
                                          borderColor: "#EEEEEE",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                          }}
                                        >
                                          {qualification ? (
                                            StatusIcon(
                                              qualification.state ?? undefined,
                                              qualification.trainingId
                                                ? qualification.expirationDate
                                                : undefined,
                                              qualification.id,
                                              installerId,
                                            )
                                          ) : (
                                            // No status row: the training is not offered
                                            // in the user's countries (single-training columns)
                                            <Tooltip
                                              title={
                                                header.isTraining
                                                  ? t("adminDashboard.notInCountryOffering")
                                                  : ""
                                              }
                                              arrow
                                            >
                                              <img
                                                src="/icon_notqualified.svg"
                                                style={{
                                                  fontFamily:
                                                    "var(--unnamed-font-family-inter)",
                                                  fontStyle:
                                                    "var(--unnamed-font-style-normal)",
                                                  fontWeight:
                                                    "var(--unnamed-font-weight-normal)",
                                                  fontSize:
                                                    "var(--unnamed-font-size-14)",
                                                  lineHeight:
                                                    "var(--unnamed-line-spacing-32)",
                                                  letterSpacing:
                                                    "var(--unnamed-character-spacing-0)",
                                                  color:
                                                    "var(--unnamed-color-1450f5)",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </Box>
                                      </td>
                                    );
                                  },
                                )}
                              </tr>
                            </>
                          ))}

                        <tr style={{ backgroundColor: "white" }}>
                          <td
                            style={{
                              border: "1px solid black",
                              borderColor: "#B2BEB5",
                              position: "sticky",
                              left: 0,
                              zIndex: 10,
                              backgroundColor: "#FFFFFF",
                              color: CONSTANTS.COLORS.BRAND_BLUE,
                              whiteSpace: "nowrap", // prevents wrapping when text is long
                              padding: "4px 8px",
                            }}
                          >
                            <Button
                              color="inherit"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenForm(!openForm);
                              }}
                              sx={{
                                // textTransform: 'none',
                                whiteSpace: "nowrap",
                                minWidth: "auto",
                                px: 1,
                              }}
                            >
                              <AddIcon />
                              <span
                                style={{
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                  position: "sticky",
                                  left: "0",
                                }}
                              >
                                <TextLabel
                                  type="smallLinkText"
                                  color={CONSTANTS.COLORS.BRAND_BLUE}
                                >
                                  {t(
                                    "adminDashboard.form.button.createNewPassport",
                                  )}
                                </TextLabel>
                              </span>
                            </Button>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                ))}
                {fetchingSuppliers && (
                  <tr>
                    <td
                      style={{
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                      colSpan={8}
                    >
                      <TextLabel type="tabHeader">
                        {t("adminDashboard.loadingSupplier")}
                      </TextLabel>
                    </td>
                  </tr>
                )}
                {selectedData && (
                  <ProfileWithQual
                    companyId={selectedCompany?.id}
                    userId={selectedFitterUserId}
                    onCloseCb={() => setSelectedData(false)}
                    country={country}
                    onUpdateInstallers={handleUpdateInstallers}
                  />
                )}
              </tbody>
            </table>
          </div>
          {loading && <Loading />}
          {openForm && (
            <AdminInstallerForm
              onCloseCb={toggleForm}
              onUpdateInstallers={handleUpdateInstallers}
              frontline={frontlineId} // Pass the selected frontline as a prop
              company={selectedCompany?.id} // Pass the selected company as a prop
              countryCode={selectedCountry || country[0]} // Pass the selected country as a prop
              region={regionId}
            />
          )}
        </>
      ) : (
        <div>{t("adminDashboard.nonAuthorizedMessage")}</div>
      )}
    </div>
  );
};
export default ScrollableTable;
